import axios from "axios";
import moment from "moment";
import JSZip from "jszip";
import {
  GetAccountAPI,
  GetAllBillsAPI,
  GenerateBillPDFAPI,
  GetEntityAPI,
} from "../config/api";
import { invTemp as invoiceTemplate } from "../assets/html/invTemp.js";
import { invTemp } from "../assets/html/invTemp.js";
import { invTempOld } from "../assets/html/invTempOld.js";
import { shouldUseNewPrintLogic } from "../config/printConfig.js";
import { dataURItoBlob, htmlTemplateToDataString } from "./index";

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Give the browser enough time to start/complete the download.
  // Revoking too quickly can cancel large downloads on some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

function safeNameForFile(input) {
  return String(input || "")
    .replace(/[^\w\s.-]/g, "")
    .trim()
    .replace(/\s+/g, "_")
    .slice(0, 100);
}

async function fetchBillsForAccount(account, startDate, endDate, entity, authToken) {
  let query = `&userId=${account._id}`;
  if (startDate && endDate) {
    const sd = moment(startDate).format("DD-MM-YYYY");
    const ed = moment(endDate).format("DD-MM-YYYY");
    query += `&startDate=${sd}&endDate=${ed}`;
  }
  const res = await axios.get(GetAllBillsAPI(entity) + query, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return res.data?.bills || [];
}

async function fetchEntityDetails(entity, authToken) {
  const res = await axios.get(GetEntityAPI(entity), {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return res.data;
}

async function fetchPartyAccount(partyAccountId, authToken) {
  const res = await axios.get(GetAccountAPI(partyAccountId), {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return res.data;
}

function buildBillHtmlTemplate({ bill, partyAccount, entityDetails, templateType = "invTemp" }) {
  const partyAddress = [
    partyAccount?.address,
    partyAccount?.city,
    partyAccount?.state,
    partyAccount?.country,
  ]
    .filter(Boolean)
    .join(",");

  const htmlData = {
    invDate: bill.invCreationDate,
    includeQRCode: false,
    clientAddress: partyAddress,
    isEligibleForIgst: partyAccount?.isEligibleForIgst,
    clientName: bill?.party?.accountName,
    clientGSTNo: partyAccount?.gstNumber,
    challanDate: moment().format("DD-MM-YYYY"),
    items: bill.items,
    // Match single-bill print: use bank details saved on the bill
    bank: bill?.bankDetail || null,
    bankQrUrl: null,
    billCode: bill.code,
    entityDetails,
    packagingCosts: bill.packagingCosts,
    packagingTaxPercents: bill.packagingTaxPercents,
    externalDiscountPercentage: bill.externalDiscountPercentage ?? 0,
  };

  let templateRenderer = templateType === "invoiceTemplate" ? invoiceTemplate : invTemp;
  if (templateType !== "invoiceTemplate" && !shouldUseNewPrintLogic(bill?.createdAt)) {
    templateRenderer = invTempOld;
  }
  return templateRenderer(htmlData);
}

async function renderBillPdfBlob({ bill, htmlTemplate, authToken }) {
  // Prefer server-side Puppeteer PDF for consistent layout.
  try {
    const res = await axios.post(
      GenerateBillPDFAPI(),
      { html: htmlTemplate },
      {
        headers: { Authorization: `Bearer ${authToken}` },
        responseType: "blob",
        timeout: 60000,
      }
    );
    const ok = res.status === 200;
    const contentType = String(res?.headers?.["content-type"] || "").toLowerCase();
    const raw =
      res.data instanceof Blob
        ? res.data
        : res.data
          ? new Blob([res.data], { type: contentType || "application/octet-stream" })
          : null;

    const isPdfType = contentType.includes("application/pdf");
    const isActualPdf =
      ok &&
      raw &&
      raw.size > 0 &&
      (await (async () => {
        try {
          const buf = await raw.slice(0, 4).arrayBuffer();
          const bytes = new Uint8Array(buf);
          return (
            bytes[0] === 0x25 &&
            bytes[1] === 0x50 &&
            bytes[2] === 0x44 &&
            bytes[3] === 0x46
          ); // %PDF
        } catch (_) {
          return false;
        }
      })());

    if (isActualPdf) {
      return new Blob([raw], { type: "application/pdf" });
    }

    // Some servers accidentally return PDF as JSON (byte-indexed object serialized as JSON string).
    if (ok && raw && raw.size > 0 && isPdfType) {
      try {
        const bodyText = await raw.text();
        if (bodyText && bodyText.trim().startsWith("{")) {
          const parsed = JSON.parse(bodyText);
          if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            const keys = Object.keys(parsed)
              .filter((k) => /^\d+$/.test(k))
              .map(Number)
              .sort((a, b) => a - b);
            if (keys.length > 1000) {
              const arr = new Uint8Array(keys.length);
              keys.forEach((i) => {
                arr[i] = parsed[i] & 0xff;
              });
              const first =
                arr[0] === 0x25 && arr[1] === 0x50 && arr[2] === 0x44 && arr[3] === 0x46;
              if (first) return new Blob([arr], { type: "application/pdf" });
            }
          }
        }
      } catch (e) {
        // fall through to browser fallback
      }
    }
  } catch (e) {
    // fallback below
  }

  // Browser fallback (may differ from server PDF)
  const options = {
    orientation: "p",
    unit: "pt",
    format: [850, 1066],
    putOnlyUsedFonts: true,
    margin: [15, 15, 15, 15],
    renderWidthPx: 1400,
    // Browser-generated PDFs do not support CSS paged counters; render page numbers via jsPDF.
    addPageNumbers: true,
    pageNumberStart: 1,
  };

  let outBlob = null;
  await htmlTemplateToDataString(htmlTemplate, options, (dataString) => {
    outBlob = dataURItoBlob(dataString);
  });
  return outBlob;
}

/**
 * Downloads already-generated bills for multiple companies, filtered by date range.
 * For each company: fetch bills list → render a combined PDF → download as a file.
 */
export async function downloadGeneratedBillsForCompanies(
  accounts,
  startDate,
  endDate,
  entity,
  authToken,
  onProgress
) {
  if (!accounts || accounts.length === 0) throw new Error("No accounts provided");
  if (!startDate || !endDate) throw new Error("Start Date and End Date are required");

  const results = [];
  const rangeLabel = `${moment(startDate).format("YYYY-MM-DD")}_${moment(endDate).format("YYYY-MM-DD")}`;
  const entityDetails = await fetchEntityDetails(entity, authToken);

  // One ZIP for everything: put ALL PDFs in the root (no per-company folders).
  const masterZip = new JSZip();
  const rootFolderName = safeNameForFile(`Bills_${rangeLabel}`);

  for (let i = 0; i < accounts.length; i++) {
    const account = accounts[i];
    const companyName = account.name || account.accountName || "";
    try {
      const bills = await fetchBillsForAccount(account, startDate, endDate, entity, authToken);
      if (!bills || bills.length === 0) {
        results.push({ company: companyName, status: "skipped", billCount: 0 });
        onProgress?.({
          company: companyName,
          status: "skipped",
          reason: "No bills found in date range",
          index: i + 1,
          total: accounts.length,
          billCount: 0,
        });
        continue;
      }

      const safeCompany = safeNameForFile(companyName || "Company");

      // Generate per-bill PDFs (same as BillList print) and add to master ZIP root.
      let addedCount = 0;
      for (let b = 0; b < bills.length; b++) {
        const bill = bills[b];
        const partyAccountId = bill?.party?.accountId;
        if (!partyAccountId) continue;
        const partyAccount = await fetchPartyAccount(partyAccountId, authToken);
        const htmlTemplate = buildBillHtmlTemplate({
          bill,
          partyAccount,
          entityDetails,
          templateType: "invTemp",
        });
        const pdfBlob = await renderBillPdfBlob({ bill, htmlTemplate, authToken });
        if (!pdfBlob || pdfBlob.size === 0) continue;
        // Include company name in filename to avoid collisions across companies.
        const fileName = `Bill_${safeCompany}_${safeNameForFile(bill.code || `#${b + 1}`)}.pdf`;
        const buf = await pdfBlob.arrayBuffer();
        masterZip.file(fileName, buf);
        addedCount += 1;
      }

      if (addedCount === 0) {
        results.push({ company: companyName, status: "skipped", billCount: 0 });
        onProgress?.({
          company: companyName,
          status: "skipped",
          reason: "No printable bills found",
          index: i + 1,
          total: accounts.length,
          billCount: 0,
        });
        continue;
      }

      results.push({ company: companyName, status: "success", billCount: bills.length });
      onProgress?.({
        company: companyName,
        status: "completed",
        index: i + 1,
        total: accounts.length,
        billCount: bills.length,
      });

      if (i < accounts.length - 1) {
        await new Promise((r) => setTimeout(r, 400));
      }
    } catch (err) {
      results.push({ company: companyName, status: "error", error: err?.message || String(err) });
      onProgress?.({
        company: companyName,
        status: "error",
        error: err?.message || String(err),
        index: i + 1,
        total: accounts.length,
      });
    }
  }

  // Download one ZIP containing all companies/bills.
  const zipBlob = await masterZip.generateAsync({ type: "blob" });
  downloadBlob(zipBlob, `${rootFolderName}.zip`);

  return {
    results,
    total: accounts.length,
    success: results.filter((r) => r.status === "success").length,
    skipped: results.filter((r) => r.status === "skipped").length,
  };
}

